import { Add, ConfirmationNumber, OpenInNew, Search } from '@mui/icons-material';
import {
    Box,
    Breadcrumbs,
    Button,
    Chip,
    FormControl,
    InputAdornment,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import api from '../../api';
import CustomPagination from '../../components/Pagination';
import Loading from '../../components/Loading';
import { DEFAULT_LIST_PAGE_SIZE, LIST_PAGE_SIZE_OPTIONS } from '../../constants/listPagination';
import { isMasterUser, patchStoredUser } from '../../helpers/authSession';
import { ticketChannelLabel, ticketStatusColor } from './ticketHelpers';

function formatDate(value) {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('pt-BR');
}

function TicketsListPage() {
    const navigate = useNavigate();
    const [isMaster, setIsMaster] = useState(isMasterUser());
    const [tickets, setTickets] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [categories, setCategories] = useState([]);
    const [channels, setChannels] = useState([]);
    const [draftSearch, setDraftSearch] = useState('');
    const [draftChannel, setDraftChannel] = useState('');
    const [draftCategory, setDraftCategory] = useState('');
    const [draftStatus, setDraftStatus] = useState('');
    const [appliedFilters, setAppliedFilters] = useState({
        search: '',
        channel: '',
        category: '',
        status: '',
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_LIST_PAGE_SIZE);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        api.get('/auth/me')
            .then((response) => {
                const user = response?.data?.data;
                if (user && typeof user.is_master === 'boolean') {
                    patchStoredUser({ is_master: user.is_master });
                    if (!cancelled) setIsMaster(user.is_master);
                }
            })
            .catch(() => {});
        api.get('/tickets/meta')
            .then((response) => {
                if (!cancelled) {
                    setStatuses(Array.isArray(response?.data?.statuses) ? response.data.statuses : []);
                    setCategories(Array.isArray(response?.data?.categories) ? response.data.categories : []);
                    setChannels(Array.isArray(response?.data?.channels) ? response.data.channels : []);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setStatuses([]);
                    setCategories([]);
                    setChannels([]);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const fetchTickets = useCallback(() => {
        setLoading(true);
        const params = {
            page: currentPage,
            limit: itemsPerPage,
        };
        if (appliedFilters.status) {
            params.status = appliedFilters.status;
        }
        if (appliedFilters.category) {
            params.category = appliedFilters.category;
        }
        if (appliedFilters.channel) {
            params.channel = appliedFilters.channel;
        }
        const q = String(appliedFilters.search || '').trim();
        if (q) {
            params.q = q;
        }
        api.get('/tickets', { params })
            .then((response) => {
                const data = response?.data?.data || {};
                setTickets(Array.isArray(data.tickets) ? data.tickets : []);
                setTotalPages(Math.max(1, Number(data.total_pages) || 1));
            })
            .catch(() => {
                setTickets([]);
                setTotalPages(1);
            })
            .finally(() => setLoading(false));
    }, [currentPage, itemsPerPage, appliedFilters]);

    useEffect(() => {
        fetchTickets();
    }, [fetchTickets]);

    const applyFilters = () => {
        setCurrentPage(1);
        setAppliedFilters({
            search: draftSearch.trim(),
            channel: draftChannel,
            category: draftCategory,
            status: draftStatus,
        });
    };

    const hasDraftOrAppliedFilters = Boolean(
        draftSearch.trim()
        || draftChannel
        || draftCategory
        || draftStatus
        || appliedFilters.search
        || appliedFilters.channel
        || appliedFilters.category
        || appliedFilters.status
    );

    const clearFilters = () => {
        setDraftSearch('');
        setDraftChannel('');
        setDraftCategory('');
        setDraftStatus('');
        setAppliedFilters({
            search: '',
            channel: '',
            category: '',
            status: '',
        });
        setCurrentPage(1);
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(Number(event.target.value) || DEFAULT_LIST_PAGE_SIZE);
        setCurrentPage(1);
    };

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link component={RouterLink} underline="hover" color="inherit" to="/admin">
                    Admin
                </Link>
                <Typography color="text.primary">Tickets</Typography>
            </Breadcrumbs>

            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} mb={3}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        {isMaster ? 'Tickets de suporte' : 'Meus tickets'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {isMaster
                            ? 'Acompanhe e responda solicitações de todos os usuários.'
                            : 'Abra um ticket para reportar erros, solicitar algo ou sugerir melhorias.'}
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/admin/tickets/new')}>
                    Novo ticket
                </Button>
            </Stack>

            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    alignItems: 'center',
                    mb: 1.5,
                }}
            >
                <TextField
                    size="small"
                    placeholder={isMaster ? 'Buscar por nome, e-mail ou assunto' : 'Buscar por assunto'}
                    value={draftSearch}
                    onChange={(e) => setDraftSearch(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            applyFilters();
                        }
                    }}
                    sx={{ flex: '1 1 240px', minWidth: 200, maxWidth: 360 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
                <FormControl size="small" sx={{ flex: '0 1 180px', minWidth: 160 }}>
                    <InputLabel id="ticket-channel-filter-label">Canal</InputLabel>
                    <Select
                        labelId="ticket-channel-filter-label"
                        label="Canal"
                        value={draftChannel}
                        onChange={(e) => setDraftChannel(e.target.value)}
                    >
                        <MenuItem value="">Todos</MenuItem>
                        {channels.map((item) => (
                            <MenuItem key={item.code} value={item.code}>
                                {item.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ flex: '0 1 180px', minWidth: 160 }}>
                    <InputLabel id="ticket-category-filter-label">Categoria</InputLabel>
                    <Select
                        labelId="ticket-category-filter-label"
                        label="Categoria"
                        value={draftCategory}
                        onChange={(e) => setDraftCategory(e.target.value)}
                    >
                        <MenuItem value="">Todas</MenuItem>
                        {categories.map((item) => (
                            <MenuItem key={item.code} value={item.code}>
                                {item.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ flex: '0 1 180px', minWidth: 160 }}>
                    <InputLabel id="ticket-status-filter-label">Status</InputLabel>
                    <Select
                        labelId="ticket-status-filter-label"
                        label="Status"
                        value={draftStatus}
                        onChange={(e) => setDraftStatus(e.target.value)}
                    >
                        <MenuItem value="">Todos</MenuItem>
                        {statuses.map((item) => (
                            <MenuItem key={item.code} value={item.code}>
                                {item.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button variant="contained" startIcon={<Search />} onClick={applyFilters}>
                    Buscar
                </Button>
                {hasDraftOrAppliedFilters ? (
                    <Button variant="text" color="inherit" onClick={clearFilters}>
                        Limpar
                    </Button>
                ) : null}
            </Box>

            {loading ? (
                <Loading />
            ) : (
                <>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Assunto</TableCell>
                                    <TableCell>Canal</TableCell>
                                    <TableCell>Categoria</TableCell>
                                    <TableCell>Status</TableCell>
                                    {isMaster ? <TableCell>Usuário</TableCell> : null}
                                    <TableCell>Atualizado</TableCell>
                                    <TableCell align="right">Abrir</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tickets.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={isMaster ? 7 : 6}>
                                            <Stack alignItems="center" spacing={1} py={4}>
                                                <ConfirmationNumber color="disabled" fontSize="large" />
                                                <Typography color="text.secondary">Nenhum ticket encontrado.</Typography>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    tickets.map((ticket) => (
                                        <TableRow
                                            key={ticket.id}
                                            hover
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => navigate(`/admin/tickets/${ticket.id}`)}
                                        >
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 320 }}>
                                                    {ticket.subject}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {ticketChannelLabel(ticket.channel, ticket.channel_label)}
                                            </TableCell>
                                            <TableCell>{ticket.category_label || ticket.category}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={ticket.status_label || ticket.status}
                                                    color={ticketStatusColor(ticket.status)}
                                                />
                                            </TableCell>
                                            {isMaster ? (
                                                <TableCell>
                                                    <Typography variant="body2">{ticket.user_name}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {ticket.user_email}
                                                    </Typography>
                                                </TableCell>
                                            ) : null}
                                            <TableCell>{formatDate(ticket.updated_at)}</TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    size="small"
                                                    endIcon={<OpenInNew />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/admin/tickets/${ticket.id}`);
                                                    }}
                                                >
                                                    Ver
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2,
                            mt: 3,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Select
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            variant="standard"
                        >
                            {LIST_PAGE_SIZE_OPTIONS.map((size) => (
                                <MenuItem key={size} value={size}>
                                    {size} por página
                                </MenuItem>
                            ))}
                        </Select>
                        {totalPages > 1 ? (
                            <CustomPagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                            />
                        ) : null}
                    </Box>
                </>
            )}
        </Box>
    );
}

export default TicketsListPage;
